import * as React from 'react';
import * as moment from 'moment';
import i18n from 'src/translation/i18n';
import OrdersTable from './components/OrdersTable';
import FiltersBar from './components/FiltersBar';

import { IOrders } from 'src/model/modules/orders.model';

import './MyOrders.scss';
import { ISelectOption } from 'src/components/common/Select2/components/SelectOption/SelectOption';
import OrderLineModel from 'src/model/orderline.model';
import PartReturnModel from 'src/model/partReturn.model';

interface IMyOrdersProps {
  orders: IOrders[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  isPending: boolean;
  match: any;
  t?: any;
  onSearch: (
    dateFrom?: string,
    dateTo?: string,
    search?: string,
    sort?: string,
    status?: string,
    size?: number,
    page?: number
  ) => void;
  cancelOrder(
    orderNumber: number,
    items: { [key: number]: string },
    currentOrdersData: {
      dateFrom?: string;
      dateTo?: string;
      search?: string;
      sort?: string;
      status?: string;
      size?: number;
      page?: number;
    }
  ): void;
}

interface IMyOrdersState {
  status: ISelectOption;
  searchOption: ISelectOption;
  searchText: string;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC' | 'AUTO';
  dateStart: string;
  dateEnd: string;
  showSlideMenu: boolean;
  searchResults: boolean; // Saves if the results where made from a search
  showClearFilter: boolean;
  dates: any;
  startExpanded: boolean;
}

class MyOrders extends React.Component<IMyOrdersProps, IMyOrdersState> {
  constructor(props: any) {
    super(props);
    this.expandAll = this.expandAll.bind(this);
    this.state = {
      sortBy: 'orderDate',
      sortOrder: 'DESC',
      status: this.defaultStatus,
      searchOption: this.defaultSearch,
      searchText: '',
      dateStart: '',
      dateEnd: '',
      showSlideMenu: false,
      searchResults: false,
      showClearFilter: false,
      dates: null,
      startExpanded: false
    };
  }

  private defaultStatus: ISelectOption = {
    name: `${this.props.t('myOrders.filterKeys.all')}`,
    value: ''
  };
  private defaultSearch: ISelectOption = {
    name: `${this.props.t('myOrders.searchByKeys.orderNumber')}`,
    value: 'orderNumber'
  };

  private expandAll = (option: boolean) => {
    this.setState({
      startExpanded: option
    });
  };

  private handleSort = (
    option: 'orderDate' | 'status',
    order: 'ASC' | 'DESC'
  ) => {
    this.props.onSearch(
      this.state.dateStart,
      this.state.dateEnd,
      this.state.searchText !== ''
        ? `${this.state.searchOption.value},${this.state.searchText}`
        : null,
      `${option},${order}`,
      this.state.status.value,
      null,
      null
    );
    this.setState({ sortBy: option, sortOrder: order, searchResults: true });
  };

  private handleSortBy = (option: 'orderDate' | 'status') => {
    let newSortOrder: 'ASC' | 'DESC' | 'AUTO' = 'ASC';
    if (this.state.sortBy === option) {
      switch (this.state.sortOrder) {
        case 'ASC':
          newSortOrder = 'DESC';
          break;
        case 'DESC':
          newSortOrder = 'AUTO';
          break;
        case 'AUTO':
          newSortOrder = 'ASC';
          break;
      }
    }
    this.setState({
      sortOrder: newSortOrder,
      sortBy: option,
      searchResults: true
    });
    this.handleSort(option, newSortOrder === 'AUTO' ? 'ASC' : newSortOrder);
  };

  private handlePagination = (pageNumber: any) => {
    this.props.onSearch(
      this.state.dateStart,
      this.state.dateEnd,
      this.state.searchText !== ''
        ? `${this.state.searchOption.value},${this.state.searchText}`
        : null,
      this.state.sortBy !== ''
        ? `${this.state.sortBy},${this.state.sortOrder}`
        : null,
      this.state.status.value,
      this.props.pageSize,
      pageNumber ? pageNumber - 1 : 0
    );
  };

  private handleMobilePagination = (type: 'next' | 'previous') => {
    if (type === 'previous') {
      this.handleGoBackPage();
    } else {
      this.handleGoForwardPage();
    }
  };

  private handleGoBackPage = () => {
    if (this.props.pageNumber > 0) {
      this.props.onSearch(
        this.state.dateStart,
        this.state.dateEnd,
        this.state.searchText !== ''
          ? `${this.state.searchOption.value},${this.state.searchText}`
          : null,
        this.state.sortBy !== ''
          ? `${this.state.sortBy},${this.state.sortOrder}`
          : null,
        this.state.status.value,
        null,
        this.props.pageNumber - 1
      );
    }
  };

  private handleGoForwardPage = () => {
    if (this.props.pageNumber < this.props.totalPages - 1) {
      this.props.onSearch(
        this.state.dateStart,
        this.state.dateEnd,
        this.state.searchText !== ''
          ? `${this.state.searchOption.value},${this.state.searchText}`
          : null,
        this.state.sortBy !== ''
          ? `${this.state.sortBy},${this.state.sortOrder}`
          : null,
        this.state.status.value,
        null,
        this.props.pageNumber + 1
      );
    }
  };

  private handleFilterByStatus = (status: ISelectOption) => {
    this.setState({ status, searchResults: true });
    this.props.onSearch(
      this.state.dateStart,
      this.state.dateEnd,
      this.state.searchText !== ''
        ? `${this.state.searchOption.value},${this.state.searchText}`
        : null,
      this.state.sortBy !== ''
        ? `${this.state.sortBy},${this.state.sortOrder}`
        : null,
      status.value,
      null,
      null
    );
  };

  private handleSearchSelect = (searchOption: ISelectOption) => {
    this.setState({ searchOption });
  };

  private handleSearchText = (e: any) => {
    this.setState({ searchText: e.target.value });
  };

  private handleOnClickSearch = () => {
    this.props.onSearch(
      this.state.dateStart,
      this.state.dateEnd,
      `${this.state.searchOption.value},${this.state.searchText}`,
      this.state.sortBy !== ''
        ? `${this.state.sortBy},${this.state.sortOrder}`
        : null,
      this.state.status.value
    );
    this.setState({ showClearFilter: true });
    this.setState({ searchResults: true });
  };

  private handleClearFilters = () => {
    this.setState({ searchText: '' });
    this.props.onSearch(
      '',
      '',
      `${this.state.searchOption.value}`,
      this.state.sortBy !== ''
        ? `${this.state.sortBy},${this.state.sortOrder}`
        : null,
      ''
    );
    this.setState({ showClearFilter: false });
    this.setState({ searchResults: true });
  };

  private onSelectDate = (dates: any) => {
    this.setState({ dates });
  };

  private handleSearchByDates = (dates: { start: any; end: any }) => {
    this.setState({ dates });
    this.props.onSearch(
      dates ? (dates.start ? dates.start.format('YYYY-MM-DD') : null) : null,
      dates ? (dates.start ? dates.end.format('YYYY-MM-DD') : null) : null,
      this.state.searchText !== ''
        ? `${this.state.searchOption.value},${this.state.searchText}`
        : null,
      this.state.sortBy !== ''
        ? `${this.state.sortBy},${this.state.sortOrder}`
        : null,
      this.state.status.value
    );
    this.setState({
      searchResults: true,
      dateStart: dates
        ? dates.start
          ? dates.start.format('YYYY-MM-DD')
          : null
        : null,
      dateEnd: dates
        ? dates.start
          ? dates.end.format('YYYY-MM-DD')
          : null
        : null
    });
  };

  private handleClearDates = () => {
    this.props.onSearch(
      null,
      null,
      this.state.searchText !== ''
        ? `${this.state.searchOption.value},${this.state.searchText}`
        : null,
      this.state.sortBy !== ''
        ? `${this.state.sortBy},${this.state.sortOrder}`
        : null,
      this.state.status.value
    );
    this.setState({
      dates: null,
      searchResults: true,
      dateStart: null,
      dateEnd: null
    });
  };

  private onClearFilters = () => {
    this.setState({
      sortBy: '',
      sortOrder: 'ASC',
      status: this.defaultStatus,
      searchOption: this.defaultSearch,
      searchText: '',
      dateEnd: '',
      dateStart: ''
    });
  };

  private handleSlideMenuSearch = (
    startDate: moment.Moment,
    endDate: moment.Moment,
    sortByOption: string = '',
    sortByOrder: 'ASC' | 'DESC' = 'ASC',
    status: ISelectOption = this.defaultStatus
  ) => {
    this.props.onSearch(
      startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate ? endDate.format('YYYY-MM-DD') : null,
      this.state.searchText !== '' &&
        `${this.state.searchOption.value},${this.state.searchText}`,
      `${sortByOption},${sortByOrder}`,
      status.value,
      null,
      null
    );
    this.setState({
      searchResults: true,
      dateStart: startDate ? startDate.format('YYYY-MM-DD') : null,
      dateEnd: endDate ? endDate.format('YYYY-MM-DD') : null,
      sortBy: sortByOption,
      sortOrder: sortByOrder,
      status
    });
    this.onClearFilters();
  };

  private onShowSlideMenu = () => {
    this.setState({ showSlideMenu: true });
  };

  private onCloseSlideMenu = () => {
    this.setState({ showSlideMenu: false });
  };

  private formatCancelItemRequest = (order: PartReturnModel) => {
    return {
      orderNumber: order.orderNumber,
      items: { [order.lineNumber]: order.productNumber }
    };
  };

  private formatCancelOrderRequest = (order: OrderLineModel[]) => {
    const items = {};
    order.map(orderLine => {
      items[orderLine.lineNumber] = orderLine.productNumber;
      return;
    });
    return {
      orderNumber: order[0].orderNumber,
      items
    };
  };

  private onSubmitCancellation = (
    order: OrderLineModel[] | PartReturnModel
  ) => {
    if (Array.isArray(order)) {
      const cancelData = this.formatCancelOrderRequest(order);
      this.props.cancelOrder(cancelData.orderNumber, cancelData.items, {
        dateFrom: this.state.dateStart,
        dateTo: this.state.dateEnd,
        search: `${this.state.searchOption.value},${this.state.searchText}`,
        sort:
          this.state.sortBy !== ''
            ? `${this.state.sortBy},${this.state.sortOrder}`
            : null,
        status: this.state.status.value,
        size: null,
        page: this.props.pageNumber
      });
    } else {
      const cancelData = this.formatCancelItemRequest(order);
      this.props.cancelOrder(cancelData.orderNumber, cancelData.items, {
        dateFrom: this.state.dateStart,
        dateTo: this.state.dateEnd,
        search: `${this.state.searchOption.value},${this.state.searchText}`,
        sort:
          this.state.sortBy !== ''
            ? `${this.state.sortBy},${this.state.sortOrder}`
            : null,
        status: this.state.status.value,
        size: null,
        page: this.props.pageNumber
      });
    }
  };

  public componentDidMount() {
    const orderNumber = this.props.match.params.orderNumber;
    // If we got an order number in the url, lets search that order number to show it
    if (orderNumber != null) {
      this.setState(
        {
          searchText: orderNumber,
          searchOption: { name: 'Order Number', value: 'orderNumber' },
          startExpanded: true
        },
        () => {
          this.handleOnClickSearch();
        }
      );
    } else {
      this.handleOnClickSearch();
    }
    this.setState({ showClearFilter: false });
  }

  public handlePageSizeChange = (pageSize: any) => {
    this.props.onSearch(
      this.state.dateStart,
      this.state.dateEnd,
      this.state.searchText !== ''
        ? `${this.state.searchOption.value},${this.state.searchText}`
        : null,
      this.state.sortBy !== ''
        ? `${this.state.sortBy},${this.state.sortOrder}`
        : null,
      this.state.status.value,
      pageSize ? pageSize : 20,
      null
    );
  };

  public render() {
    const { t } = this.props;
    return (
      <div className="rp-my-orders">
        <div className="rp-my-orders-title">
          <h4>{t('myOrders.myOrdersTitle')} </h4>
        </div>
        {this.props.isPending ? (
          <div className="loader-container">
            <div className="loader" />
          </div>
        ) : (
          <React.Fragment>
            <div className="rp-my-orders-filter-bar">
              <FiltersBar
                showSlideMenu={this.state.showSlideMenu}
                showClearFilter={this.state.showClearFilter}
                status={this.state.status}
                searchOption={this.state.searchOption}
                searchText={this.state.searchText}
                dates={this.state.dates}
                handleFilterByStatus={this.handleFilterByStatus}
                handleSearchSelect={this.handleSearchSelect}
                handleSearchText={this.handleSearchText}
                handleOnClickSearch={this.handleOnClickSearch}
                handleClearFilters={this.handleClearFilters}
                handleSearchByDates={this.handleSearchByDates}
                handleClearDates={this.handleClearDates}
                onCloseSlideMenu={this.onCloseSlideMenu}
                onOpenSlideMenu={this.onShowSlideMenu}
                handleSearch={this.handleSlideMenuSearch}
                setDates={this.onSelectDate}
              />

              {!this.state.showSlideMenu && (
                <OrdersTable
                  totalPages={this.props.totalPages}
                  pageNumber={this.props.pageNumber}
                  pageSize={this.props.pageSize}
                  orders={this.props.orders}
                  sortOrder={this.state.sortOrder}
                  sortBy={this.state.sortBy}
                  searchResults={this.state.searchResults}
                  handleSortBy={this.handleSortBy}
                  handleGoBackPage={this.handleGoBackPage}
                  handleGoForwardPage={this.handleGoForwardPage}
                  startExpanded={this.state.startExpanded}
                  expandAllChild={this.expandAll}
                  handlePagination={this.handlePagination}
                  onSubmitCancellation={this.onSubmitCancellation}
                  handlePageSizeChange={this.handlePageSizeChange}
                  handleMobilePagination={this.handleMobilePagination}
                />
              )}
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }
}
export default MyOrders;
